import { CommonModule } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextarea } from 'primeng/inputtextarea';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TagModule } from 'primeng/tag';
import { forkJoin, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { TestModule } from '../../models/testing/test-catalogs.model';
import { ReportMode, TestFunctionality, TestSubmodule } from '../../models/testing/test-location.model';
import { TestFinding } from '../../models/testing/test-plan.model';
import { IssueContext, IssueContextService } from '../../services/testing/issue-context.service';
import { IssueReporterService } from '../../services/testing/issue-reporter.service';
import { SupportAccessService } from '../../services/testing/support-access.service';
import { TestFindingService } from '../../services/testing/test-finding.service';
import { TestLocationService } from '../../services/testing/test-location.service';

/**
 * Entrada global de soporte: stack de FABs semitransparentes + atajo Ctrl/⌘ + I.
 * Vive en AppLayout, así que está disponible en toda la app autenticada.
 *
 * De abajo hacia arriba: Reportar (siempre), Mis errores reportados (si el usuario
 * tiene ≥1 reporte) y Administrar errores (si está en la allowlist del backend y hay
 * pendientes). La visibilidad la decide `SupportAccessService`.
 */
@Component({
  selector: 'app-issue-reporter',
  standalone: true,
  imports: [
    CommonModule, FormsModule, DialogModule, DropdownModule, ButtonModule,
    SelectButtonModule, TagModule, InputTextModule, InputTextarea
  ],
  templateUrl: './issue-reporter.component.html',
  styleUrls: ['./issue-reporter.component.scss']
})
export class IssueReporterComponent implements OnInit, OnDestroy {
  visible = false;
  submitting = false;
  dragOver = false;

  ctx!: IssueContext;
  form: Partial<TestFinding> = {};
  files: File[] = [];

  modules: TestModule[] = [];
  submodules: TestSubmodule[] = [];
  functionalities: TestFunctionality[] = [];

  /** F1 queda tras bandera: el navegador/OS suele reservarlo. */
  enableF1 = false;

  readonly modeOptions = [
    { label: 'Soporte', value: ReportMode.Soporte },
    { label: 'Testing', value: ReportMode.Testing }
  ];

  readonly typeOptions = [
    { label: 'Bug', value: 0, icon: 'pi pi-times-circle' },
    { label: 'Nuevo requerimiento', value: 1, icon: 'pi pi-plus-circle' },
    { label: 'Mejora', value: 2, icon: 'pi pi-arrow-up-right' },
    { label: 'Consulta', value: 3, icon: 'pi pi-question-circle' }
  ];

  readonly severityOptions = [
    { label: 'Baja', value: 0 },
    { label: 'Media', value: 1 },
    { label: 'Alta', value: 2 },
    { label: 'Crítica', value: 3 }
  ];

  readonly priorityOptions = [
    { label: 'Baja', value: 0 },
    { label: 'Media', value: 1 },
    { label: 'Alta', value: 2 },
    { label: 'Urgente', value: 3 }
  ];

  private readonly subs = new Subscription();

  constructor(
    private ctxSvc: IssueContextService,
    private loc: TestLocationService,
    private findingSvc: TestFindingService,
    private msg: MessageService,
    private router: Router,
    public support: SupportAccessService,
    reporter: IssueReporterService
  ) {
    this.subs.add(reporter.openRequests$.subscribe(() => this.open()));
  }

  ngOnInit() {
    this.support.refresh();

    // Al navegar, los contadores pueden haber cambiado (otro usuario resolvió algo,
    // o el propio usuario acaba de reportar desde otra pantalla).
    this.subs.add(
      this.router.events
        .pipe(filter(e => e instanceof NavigationEnd))
        .subscribe(() => this.support.refresh())
    );
  }

  ngOnDestroy() { this.subs.unsubscribe(); }

  goToMyIssues() { this.router.navigate(['/testing/my-issues']); }

  goToAdmin() { this.router.navigate(['/testing/findings']); }

  /**
   * Angular ignora `navigate()` a la URL en la que ya estás (onSameUrlNavigation
   * default: 'ignore'), así que el FAB debe ocultarse en su propio destino o el
   * clic no hace nada visible.
   */
  get onAdminScreen(): boolean { return this.currentPath === '/testing/findings'; }

  get onMyIssuesScreen(): boolean { return this.currentPath === '/testing/my-issues'; }

  private get currentPath(): string { return this.router.url.split('?')[0].split('#')[0]; }

  @HostListener('window:keydown', ['$event'])
  onKey(e: KeyboardEvent) {
    if ((e.ctrlKey || e.metaKey) && (e.key === 'i' || e.key === 'I')) { e.preventDefault(); this.open(); }
    if (this.enableF1 && e.key === 'F1') { e.preventDefault(); this.open(); }
    if (e.key === 'Escape' && this.visible) this.visible = false;
  }

  /** Pegar una captura desde el portapapeles la adjunta directamente. */
  @HostListener('window:paste', ['$event'])
  onPaste(e: ClipboardEvent) {
    if (!this.visible) return;
    const items = Array.from(e.clipboardData?.items ?? []);
    for (const item of items) {
      if (item.type.indexOf('image') !== 0) continue;
      const file = item.getAsFile();
      if (file) this.addFile(this.renameIfPasted(file));
    }
  }

  open() {
    if (this.visible) return;

    this.ctx = this.ctxSvc.capture();
    this.files = [];
    this.submodules = [];
    this.functionalities = [];
    this.form = {
      testRunId: undefined,
      reportMode: ReportMode.Soporte,
      findingType: 0,
      findingSeverity: 1,
      findingPriority: 1,
      findingTitle: '',
      description: '',
      projectId: this.ctx.projectId,
      detectedRoute: this.ctx.detectedRoute,
      detectedScreen: this.ctx.detectedScreen,
      userAgent: this.ctx.userAgent,
      viewport: this.ctx.viewport
    };
    this.visible = true;

    this.loc.getModules().subscribe({
      next: modules => { this.modules = modules; this.preselectFromSuggestion(); },
      error: () => this.modules = []
    });
  }

  close() { this.visible = false; }

  /** Casa la sugerencia de la tabla de mapeo con el catálogo, por nombre. */
  private preselectFromSuggestion() {
    if (!this.ctx.suggestedModuleName) return;

    const module = this.modules.find(m => this.sameName(m.name, this.ctx.suggestedModuleName!));
    if (!module) return;

    this.form.moduleId = module.testModuleId;
    this.loadSubmodules(module.testModuleId, () => {
      if (!this.ctx.suggestedSubmoduleName) return;
      const sub = this.submodules.find(s => this.sameName(s.name, this.ctx.suggestedSubmoduleName!));
      if (!sub) return;
      this.form.submoduleId = sub.testSubmoduleId;
      this.loadFunctionalities(sub.testSubmoduleId);
    });
  }

  private sameName(a: string, b: string): boolean {
    return (a ?? '').trim().toLocaleLowerCase() === (b ?? '').trim().toLocaleLowerCase();
  }

  onModuleChange() {
    this.form.submoduleId = undefined;
    this.form.functionalityId = undefined;
    this.submodules = [];
    this.functionalities = [];
    if (this.form.moduleId) this.loadSubmodules(this.form.moduleId);
  }

  onSubmoduleChange() {
    this.form.functionalityId = undefined;
    this.functionalities = [];
    if (this.form.submoduleId) this.loadFunctionalities(this.form.submoduleId);
  }

  private loadSubmodules(moduleId: number, done?: () => void) {
    this.loc.getSubmodules(moduleId).subscribe({
      next: data => { this.submodules = data; done?.(); },
      error: () => this.submodules = []
    });
  }

  private loadFunctionalities(submoduleId: number) {
    this.loc.getFunctionalities(submoduleId).subscribe({
      next: data => this.functionalities = data,
      error: () => this.functionalities = []
    });
  }

  // ── Adjuntos ────────────────────────────────────────────────────────────
  onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    Array.from(input.files ?? []).forEach(f => this.addFile(f));
    input.value = '';
  }

  onDragOver(e: DragEvent) { e.preventDefault(); this.dragOver = true; }
  onDragLeave(e: DragEvent) { e.preventDefault(); this.dragOver = false; }

  onDrop(e: DragEvent) {
    e.preventDefault();
    this.dragOver = false;
    Array.from(e.dataTransfer?.files ?? []).forEach(f => this.addFile(f));
  }

  removeFile(index: number) { this.files.splice(index, 1); }

  private addFile(file: File) { this.files.push(file); }

  /** Una imagen pegada llega como "image.png"; le damos un nombre único. */
  private renameIfPasted(file: File): File {
    if (file.name && file.name !== 'image.png') return file;
    const ext = (file.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
    return new File([file], `captura-${Date.now()}.${ext}`, { type: file.type });
  }

  formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  // ── Envío ───────────────────────────────────────────────────────────────
  get canSubmit(): boolean {
    return !this.submitting
      && !!this.form.findingTitle?.trim()
      && !!this.form.description?.trim();
  }

  submit() {
    if (!this.canSubmit) return;
    this.submitting = true;

    // reportedByUserId lo resuelve el backend desde el token.
    this.findingSvc.add(this.form as TestFinding).subscribe({
      next: created => this.uploadEvidences(created),
      error: () => {
        this.submitting = false;
        this.msg.add({ severity: 'error', summary: 'No se pudo reportar', detail: 'Intenta nuevamente en unos segundos.' });
      }
    });
  }

  private uploadEvidences(created: TestFinding) {
    if (!this.files.length) { this.finish(created); return; }

    forkJoin(this.files.map(f => this.findingSvc.uploadEvidence(created.testFindingId, f))).subscribe({
      next: () => this.finish(created),
      error: () => {
        // El issue ya quedó creado: no se pierde el reporte aunque falle una captura.
        this.submitting = false;
        this.visible = false;
        this.support.refresh();
        this.msg.add({
          severity: 'warn',
          summary: `Issue #${created.testFindingId} creado`,
          detail: 'Alguna captura no se pudo subir. Puedes adjuntarla desde la mesa de issues.'
        });
      }
    });
  }

  private finish(created: TestFinding) {
    this.submitting = false;
    this.visible = false;
    // Si era su primer reporte, esto hace aparecer el FAB "Mis errores reportados".
    this.support.refresh();
    this.msg.add({
      severity: 'success',
      summary: `Issue #${created.testFindingId} reportado`,
      detail: 'Gracias. El equipo de soporte ya lo tiene en la mesa.'
    });
  }
}
